import { FormInstance } from 'ssc-ui-react/typings/components/form/types';

import { createSelectors } from '@/store/constructor/createSelectors';
import { createStore } from '@/store/constructor/createStore';

import { getOperatorPerformanceLastTenHours, GetOperatorPerformanceLastTenHoursParams } from '../api';
import { OperatorPerformanceLastTenHoursItem, OperatorPerformanceLastTenHoursSorterModel } from '../defined';

type State = {
  loading: boolean;
  form?: FormInstance;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
  };
  items: OperatorPerformanceLastTenHoursItem[];
  sorterModel: OperatorPerformanceLastTenHoursSorterModel;
};

type Action = {
  getOperatorPerformanceLastTenHoursAsync: (params: GetOperatorPerformanceLastTenHoursParams) => Promise<void>;
  getSorterModel: () => OperatorPerformanceLastTenHoursSorterModel;
  setSorterModel: (sorterModel: OperatorPerformanceLastTenHoursSorterModel) => void;
  setForm: (lastHourForm?: FormInstance) => void;
  getForm: () => FormInstance | undefined;
  setPagination: (pagination: { current: number; pageSize: number; total: number }) => void;
  getPagination: () => { current: number; pageSize: number; total: number };
};

export const defaultSortModel = Array(10).fill(undefined).reduce((acc, _, index) => {
  acc[index] = undefined;
  return acc;
}, {});

const useLastTenHoursOperatorPerformanceStore = createSelectors(
  createStore<State & Action>((set, get) => {
    return {
      loading: false,
      form: undefined,
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
      },
      items: [],
      sorterModel: defaultSortModel,
      getSorterModel: () => get().sorterModel,
      setSorterModel: (sorterModel) => set({ sorterModel }),
      setForm: (form) => set({ form }),
      getForm: () => get().form,
      getItems: () => get().items,
      setPagination: (pagination) => set({ pagination }),
      getPagination: () => get().pagination,
      getOperatorPerformanceLastTenHoursAsync: async (params) => {
        try {
          set({ loading: true });
          const sortModel = get().sorterModel;
          const targetKey = Object.keys(sortModel).find(key => sortModel[key] !== undefined);
          const dir = targetKey ? sortModel[targetKey] : undefined;
          const order = dir === 'ascend'
            ? 0
            : dir === 'descend' ? 1 : undefined;
          const selectMeetTarget = get().form?.getFieldValue('select_meet_target');
          const bizWorkstationIdList = get().form?.getFieldValue('biz_workstation_id_list') as string[] | undefined;
          const operatorIdList = get().form?.getFieldValue('operator_id_list') as string[] | undefined;
          const defaultParams = {
            pageno: get().pagination.current,
            count: get().pagination.pageSize,
            select_meet_target: selectMeetTarget,
            biz_workstation_id_list: bizWorkstationIdList?.length ? bizWorkstationIdList : undefined,
            operator_id_list: operatorIdList?.length ? operatorIdList : undefined,
            order_by_time_index: targetKey ? Number(targetKey) : undefined,
            order_by_sequence: order,
          };
          const res = await getOperatorPerformanceLastTenHours({ ...defaultParams, ...params });
          set({
            pagination: {
              ...get().pagination,
              total: res.data?.total || 0,
              current: res.data?.pageno || 1,
            },
            items: res.data?.items || [],
          });
        } catch (error) {
          console.error(error);
        } finally {
          set({ loading: false });
        }
      },
    };
}));

export default useLastTenHoursOperatorPerformanceStore;
